
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator } from
'@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, User, Settings, Menu, Shield, Users, UserCog } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const Navbar = () => {
  const { user, logout, isSuperAdmin, isAdmin, adminStatus } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getInitials = (name: string) => {
    return name.
    split(' ').
    map((word) => word.charAt(0).toUpperCase()).
    join('').
    slice(0, 2);
  };

  const navigationItems = [
  { name: 'Home', href: '/' },
  { name: 'Features', href: '/#features' },
  { name: 'About', href: '/#about' },
  { name: 'Contact', href: '/#contact' }];


  // Check if user has admin access (AdminL1, AdminL2, or SuperAdmin)
  const hasAdminAccess = isSuperAdmin || adminStatus.is_admin;

  const getAdminBadgeColor = () => {
    if (isSuperAdmin) return 'bg-red-50 text-red-700 border-red-200';
    if (isAdmin && adminStatus.admin_level === 2) return 'bg-orange-50 text-orange-700 border-orange-200';
    if (isAdmin) return 'bg-purple-50 text-purple-700 border-purple-200';
    return '';
  };

  const getAdminLabel = () => {
    if (isSuperAdmin) return 'Super Admin';
    if (isAdmin && adminStatus.admin_level === 2) return 'Admin L2';
    if (isAdmin) return 'Admin L1';
    return '';
  };

  const getAvatarColor = () => {
    if (isSuperAdmin) return 'bg-gradient-to-r from-red-500 to-red-600';
    if (isAdmin && adminStatus.admin_level === 2) return 'bg-gradient-to-r from-orange-500 to-orange-600';
    if (isAdmin) return 'bg-gradient-to-r from-purple-500 to-purple-600';
    return 'bg-gradient-to-r from-indigo-500 to-purple-500';
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50" data-id="x8xh6xuhu" data-path="src/components/layout/Navbar.tsx">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="h77fbrzi4" data-path="src/components/layout/Navbar.tsx">
        <div className="flex justify-between items-center h-16" data-id="j6p5u5z3z" data-path="src/components/layout/Navbar.tsx">
          {/* Logo */}
          <div className="flex-shrink-0" data-id="06dfb039e" data-path="src/components/layout/Navbar.tsx">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent" data-id="ed0qj37ys" data-path="src/components/layout/Navbar.tsx">
              Soyous AI
            </Link>
          </div>

          {/* Desktop Navigation */}
          












          {/* Auth Section */}
          <div className="hidden md:flex items-center space-x-4" data-id="qgb56tgup" data-path="src/components/layout/Navbar.tsx">
            {user ?
            <div className="flex items-center space-x-3" data-id="n1d0sopox" data-path="src/components/layout/Navbar.tsx">
                {/* Admin/SuperAdmin Badge */}
                {(isAdmin || isSuperAdmin) &&
              <Badge variant="outline" className={getAdminBadgeColor()} data-id="lz15z6kbp" data-path="src/components/layout/Navbar.tsx">
                    {isSuperAdmin ? <Shield className="w-3 h-3 mr-1" data-id="ymzepird1" data-path="src/components/layout/Navbar.tsx" /> : <UserCog className="w-3 h-3 mr-1" data-id="zf2epr6d9" data-path="src/components/layout/Navbar.tsx" />}
                    {getAdminLabel()}
                  </Badge>
              }

                <DropdownMenu data-id="vndc8gn6u" data-path="src/components/layout/Navbar.tsx">
                  <DropdownMenuTrigger asChild data-id="20jv2etb8" data-path="src/components/layout/Navbar.tsx">
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full" data-id="nxz8msdsu" data-path="src/components/layout/Navbar.tsx">
                      <Avatar className="h-8 w-8" data-id="45g3e9839" data-path="src/components/layout/Navbar.tsx">
                        <AvatarFallback className={`text-white text-sm ${getAvatarColor()}`} data-id="dxvtzg9gx" data-path="src/components/layout/Navbar.tsx">
                          {getInitials(user.Name)}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount data-id="1bznn4wcd" data-path="src/components/layout/Navbar.tsx">
                    <div className="flex items-center justify-start gap-2 p-2" data-id="atoj1dvet" data-path="src/components/layout/Navbar.tsx">
                      <div className="flex flex-col space-y-1 leading-none" data-id="7twykmuj6" data-path="src/components/layout/Navbar.tsx">
                        <p className="font-medium" data-id="czfah859k" data-path="src/components/layout/Navbar.tsx">{user.Name}</p>
                        <p className="w-[200px] truncate text-sm text-muted-foreground" data-id="8aidek1zk" data-path="src/components/layout/Navbar.tsx">
                          {user.Email}
                        </p>
                        {(isAdmin || isSuperAdmin) &&
                      <Badge variant="outline" className={`${getAdminBadgeColor()} text-xs w-fit`} data-id="5nf67hxvn" data-path="src/components/layout/Navbar.tsx">
                            {getAdminLabel()}
                            {isAdmin && !isSuperAdmin && adminStatus.admin_role && ` - ${adminStatus.admin_role}`}
                          </Badge>
                      }
                      </div>
                    </div>
                    <DropdownMenuSeparator data-id="espzlam7j" data-path="src/components/layout/Navbar.tsx" />

                    {/* Regular Dashboard */}
                    <DropdownMenuItem onClick={() => navigate('/app')} data-id="4lzipc7fd" data-path="src/components/layout/Navbar.tsx">
                      <User className="mr-2 h-4 w-4" data-id="tw7pnm0zc" data-path="src/components/layout/Navbar.tsx" />
                      Dashboard
                    </DropdownMenuItem>

                    {/* Admin Panel - Show for all admin levels and SuperAdmins */}
                    {hasAdminAccess &&
                  <DropdownMenuItem onClick={() => navigate('/superadmin')} data-id="zysqga97d" data-path="src/components/layout/Navbar.tsx">
                        <Shield className="mr-2 h-4 w-4" data-id="zwwynntkq" data-path="src/components/layout/Navbar.tsx" />
                        {isSuperAdmin ? 'Super Admin Panel' : 'Admin Panel'}
                      </DropdownMenuItem>
                  }

                    <DropdownMenuItem onClick={() => navigate('/settings')} data-id="iam3m7cw4" data-path="src/components/layout/Navbar.tsx">
                      <Settings className="mr-2 h-4 w-4" data-id="fb7rjhnc7" data-path="src/components/layout/Navbar.tsx" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator data-id="owcuazbz7" data-path="src/components/layout/Navbar.tsx" />
                    <DropdownMenuItem onClick={handleLogout} data-id="ebwl99b92" data-path="src/components/layout/Navbar.tsx">
                      <LogOut className="mr-2 h-4 w-4" data-id="ofadrx5t4" data-path="src/components/layout/Navbar.tsx" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div> :

            <div className="flex items-center space-x-2" data-id="7hmawp0nf" data-path="src/components/layout/Navbar.tsx">
                <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-indigo-600" data-id="of4nhms3d" data-path="src/components/layout/Navbar.tsx">
                  Sign In
                </Button>
                <Button
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white" data-id="flq4hdgyh" data-path="src/components/layout/Navbar.tsx">
                  Get Started
                </Button>
              </div>
            }
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden" data-id="bii8es0uh" data-path="src/components/layout/Navbar.tsx">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen} data-id="ec7ktwf9l" data-path="src/components/layout/Navbar.tsx">
              <SheetTrigger asChild data-id="69jc3ctdr" data-path="src/components/layout/Navbar.tsx">
                <Button variant="ghost" size="sm" data-id="7mpyv85s5" data-path="src/components/layout/Navbar.tsx">
                  <Menu className="h-6 w-6" data-id="x8i1yawfl" data-path="src/components/layout/Navbar.tsx" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]" data-id="bra387rfg" data-path="src/components/layout/Navbar.tsx">
                <div className="flex flex-col space-y-4 mt-4" data-id="s26xircjk" data-path="src/components/layout/Navbar.tsx">
                  {navigationItems.map((item) =>
                  <Link
                    key={item.name}
                    to={item.href}
                    className="text-gray-600 hover:text-indigo-600 px-3 py-2 rounded-md text-base font-medium transition-colors duration-200"
                    onClick={() => setIsMobileMenuOpen(false)} data-id="5s4ydzcxd" data-path="src/components/layout/Navbar.tsx">
                      {item.name}
                    </Link>
                  )}

                  <div className="border-t pt-4" data-id="1mjl5k8c5" data-path="src/components/layout/Navbar.tsx">
                    {user ?
                    <div className="space-y-2" data-id="qpux3mw2u" data-path="src/components/layout/Navbar.tsx">
                        <div className="flex items-center space-x-3 px-3 py-2" data-id="twvtdzxgy" data-path="src/components/layout/Navbar.tsx">
                          <Avatar className="h-8 w-8" data-id="dy8rjv2my" data-path="src/components/layout/Navbar.tsx">
                            <AvatarFallback className={`text-white text-sm ${getAvatarColor()}`} data-id="edchzgh9f" data-path="src/components/layout/Navbar.tsx">
                              {getInitials(user.Name)}
                            </AvatarFallback>
                          </Avatar>
                          <div data-id="e8wl7bn1j" data-path="src/components/layout/Navbar.tsx">
                            <p className="font-medium text-sm" data-id="4l7dpnt7m" data-path="src/components/layout/Navbar.tsx">{user.Name}</p>
                            <p className="text-xs text-gray-500 truncate" data-id="4p1ul369s" data-path="src/components/layout/Navbar.tsx">{user.Email}</p>
                            {(isAdmin || isSuperAdmin) &&
                          <Badge variant="outline" className={`${getAdminBadgeColor()} text-xs mt-1`} data-id="otxf0ofby" data-path="src/components/layout/Navbar.tsx">
                                {getAdminLabel()}
                              </Badge>
                          }
                          </div>
                        </div>

                        <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate('/app');
                          setIsMobileMenuOpen(false);
                        }} data-id="8l9xm1sqw" data-path="src/components/layout/Navbar.tsx">
                          <User className="mr-2 h-4 w-4" data-id="52ptey944" data-path="src/components/layout/Navbar.tsx" />
                          Dashboard
                        </Button>

                        {/* Admin Panel - Show for all admin levels and SuperAdmins */}
                        {hasAdminAccess &&
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate('/superadmin');
                          setIsMobileMenuOpen(false);
                        }} data-id="4808zxv5u" data-path="src/components/layout/Navbar.tsx">
                            <Shield className="mr-2 h-4 w-4" data-id="0002nc9if" data-path="src/components/layout/Navbar.tsx" />
                            {isSuperAdmin ? 'Super Admin Panel' : 'Admin Panel'}
                          </Button>
                      }

                        <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          navigate('/settings');
                          setIsMobileMenuOpen(false);
                        }} data-id="hs5wmd9c0" data-path="src/components/layout/Navbar.tsx">
                          <Settings className="mr-2 h-4 w-4" data-id="4j8n33syj" data-path="src/components/layout/Navbar.tsx" />
                          Settings
                        </Button>

                        <Button
                        variant="ghost"
                        className="w-full justify-start text-red-600 hover:text-red-700"
                        onClick={() => {
                          handleLogout();
                          setIsMobileMenuOpen(false);
                        }} data-id="oe4oksobc" data-path="src/components/layout/Navbar.tsx">
                          <LogOut className="mr-2 h-4 w-4" data-id="8ss3dy2is" data-path="src/components/layout/Navbar.tsx" />
                          Log out
                        </Button>
                      </div> :

                    <div className="space-y-2" data-id="6cakgaayq" data-path="src/components/layout/Navbar.tsx">
                        <Button
                        variant="ghost"
                        className="w-full"
                        onClick={() => {
                          navigate('/login');
                          setIsMobileMenuOpen(false);
                        }} data-id="v7uce3mhx" data-path="src/components/layout/Navbar.tsx">
                          Sign In
                        </Button>
                        <Button
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                        onClick={() => {
                          navigate('/register');
                          setIsMobileMenuOpen(false);
                        }} data-id="9evak4msi" data-path="src/components/layout/Navbar.tsx">
                          Get Started
                        </Button>
                      </div>
                    }
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>);

};

export default Navbar;